/**
 * Authentication API client
 */

import {
	authErrorMessage,
	friendlyStatusMessage,
} from "../utils/apiErrorDetail";
import { apiFetch } from "../utils/apiClient";
import { getApiUrl } from "../utils/env";
import {
	clearSupportToken,
	getSessionToken,
	isSupportSession,
} from "../utils/sessionToken";

// Resolve API base at call time to pick up runtime env (window.__ENV__) reliably

export interface User {
	id: string;
	email: string;
	is_active: boolean;
	is_superuser: boolean;
	is_verified: boolean;
	full_name?: string;
}

export interface LoginResponse {
	access_token: string;
	token_type: string;
}

export interface RegisterRequest {
	email: string;
	password: string;
	full_name?: string;
}

export interface LoginRequest {
	username: string;
	password: string;
}

class AuthService {
	private getV3AuthBaseUrl(): string {
		const baseUrl = getApiUrl();
		if (!baseUrl) {
			return "/api/v3/auth";
		}
		if (baseUrl.endsWith("/api/v1")) {
			return `${baseUrl.slice(0, -7)}/api/v3/auth`;
		}
		return `${baseUrl}/api/v3/auth`;
	}

	private getAuthHeader(): Record<string, string> {
		const token = this.getToken();
		return token ? { Authorization: `Bearer ${token}` } : {};
	}

	// Token management
	getToken(): string | null {
		return getSessionToken();
	}

	setToken(token: string): void {
		localStorage.setItem("access_token", token);
	}

	removeToken(): void {
		// In a support session (DOP-562) "log out" ends the session and nothing
		// else: `access_token` is the operator's own login, not the session's.
		if (isSupportSession()) {
			clearSupportToken();
			return;
		}
		console.log("[Auth] Removing access_token from localStorage");
		localStorage.removeItem("access_token");
		// Verify it was removed
		const stillThere = localStorage.getItem("access_token");
		if (stillThere) {
			console.error("[Auth] ERROR: Token still in localStorage after removal!");
		} else {
			console.log("[Auth] Token successfully removed from localStorage");
		}
	}

	isAuthenticated(): boolean {
		const hasToken = !!this.getToken();
		console.log("[Auth] isAuthenticated check:", hasToken);
		return hasToken;
	}

	// Register with email/password
	async register(data: RegisterRequest): Promise<User> {
		let response: Response;
		try {
			response = await fetch(`${this.getV3AuthBaseUrl()}/register`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(data),
			});
		} catch (networkError) {
			console.error("Network error during registration:", networkError);
			throw new Error(
				"Cannot connect to server. Please check if the backend is running.",
			);
		}

		if (!response.ok) {
			let errorMessage = "We could not create that account. Please try again.";
			try {
				const error = await response.json();
				// A refusal with no resolvable workspace (DOP-222) arrives as
				// `{code, message}`; `error.detail || …` used to put
				// "[object Object]" on the register screen.
				// A bare fastapi-users code gets mapped to copy, never shown raw.
				errorMessage = authErrorMessage(error.detail, errorMessage);
			} catch (jsonError) {
				// If response is not JSON (e.g., HTML error page), use status text
				errorMessage = friendlyStatusMessage(response.status);
			}
			throw new Error(errorMessage);
		}

		try {
			return await response.json();
		} catch (jsonError) {
			console.error("Registration response is not valid JSON:", jsonError);
			throw new Error("Server returned invalid response format");
		}
	}

	/**
	 * What this deployment asks of a new account before it can be used.
	 *
	 * `register` returns `is_active: false` whether an emailed code or an
	 * operator is holding the account, and the two need different screens --
	 * rendering a code box where nobody will ever send a code leaves the person
	 * waiting on a mail that does not exist. Falls back to "approval", the
	 * screen that tells them to ask a human, because that is the honest answer
	 * when we could not find out.
	 */
	async getSignupPolicy(): Promise<"code" | "approval" | "none"> {
		try {
			const response = await fetch(`${this.getV3AuthBaseUrl()}/signup-policy`);
			if (!response.ok) return "approval";
			const body = await response.json();
			return body?.verification === "code"
				? "code"
				: body?.verification === "none"
					? "none"
					: "approval";
		} catch (networkError) {
			console.error("Could not read the signup policy:", networkError);
			return "approval";
		}
	}

	/** Redeem the emailed signup code. Throws with copy fit to show a person. */
	async verifySignupCode(email: string, code: string): Promise<void> {
		let response: Response;
		try {
			response = await fetch(`${this.getV3AuthBaseUrl()}/signup-code/verify`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email, code }),
			});
		} catch (networkError) {
			console.error("Network error verifying signup code:", networkError);
			throw new Error(
				"Cannot connect to server. Please check your connection and try again.",
			);
		}

		if (!response.ok) {
			let errorMessage = "That code did not work. Please try again.";
			try {
				const error = await response.json();
				errorMessage = authErrorMessage(error.detail, errorMessage);
			} catch (jsonError) {
				errorMessage = friendlyStatusMessage(response.status);
			}
			throw new Error(errorMessage);
		}
	}

	/**
	 * Ask for a fresh code.
	 *
	 * Resolves whatever the server says. It answers 202 to everything on
	 * purpose -- it will not reveal whether an address has an account here --
	 * so there is no success to report beyond "we asked".
	 */
	async resendSignupCode(email: string): Promise<void> {
		try {
			await fetch(`${this.getV3AuthBaseUrl()}/signup-code/resend`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email }),
			});
		} catch (networkError) {
			console.error("Network error resending signup code:", networkError);
			throw new Error(
				"Cannot connect to server. Please check your connection and try again.",
			);
		}
	}

	// Login with email/password
	async login(email: string, password: string): Promise<LoginResponse> {
		const formData = new URLSearchParams();
		formData.append("username", email);
		formData.append("password", password);

		let response: Response;
		try {
			response = await fetch(`${this.getV3AuthBaseUrl()}/jwt/login`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: formData,
			});
		} catch (networkError) {
			console.error("Network error during login:", networkError);
			throw new Error(
				"Cannot connect to server. Please check if the backend is running.",
			);
		}

		if (!response.ok) {
			let errorMessage = "We could not sign you in. Please try again.";
			try {
				const error = await response.json();
				// fastapi-users answers with a bare code - {"detail":
				// "LOGIN_BAD_CREDENTIALS"} - and this used to be `error.detail ||
				// ...`, so the most common failure in the product put a machine
				// constant on the login screen.
				console.log("Login error:", error);
				errorMessage = authErrorMessage(error.detail, errorMessage);
			} catch (jsonError) {
				// If response is not JSON (e.g., HTML error page from proxy/nginx), use status text
				console.log(
					"Login error - non-JSON response:",
					response.status,
					response.statusText,
				);
				errorMessage = friendlyStatusMessage(response.status);
			}
			throw new Error(errorMessage);
		}

		let data;
		try {
			data = await response.json();
			console.log("Raw login response:", data);
		} catch (jsonError) {
			console.error("Login response is not valid JSON:", jsonError);
			throw new Error("Server returned invalid response format");
		}

		// Validate that we received an access token
		if (!data.access_token) {
			console.error("Login response missing access_token:", data);
			throw new Error("No access token received from server");
		}

		console.log("Setting token:", data.access_token);
		this.setToken(data.access_token);
		console.log("Token set successfully");
		return data;
	}

	// Logout
	async logout(): Promise<void> {
		const token = this.getToken();
		if (token) {
			try {
				await fetch(`${this.getV3AuthBaseUrl()}/jwt/logout`, {
					method: "POST",
					headers: this.getAuthHeader(),
				});
			} catch (error) {
				console.error("Logout error:", error);
			}
		}
		this.removeToken();
	}

	// Get current user
	async getCurrentUser(): Promise<User> {
		// Use apiFetch which automatically handles 401 responses
		const response = await apiFetch("/api/v3/auth/users/me", {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error("Failed to fetch user");
		}

		try {
			return await response.json();
		} catch (jsonError) {
			console.error("getCurrentUser response is not valid JSON:", jsonError);
			throw new Error("Server returned invalid response format");
		}
	}

	// Update current user
	async updateUser(data: Partial<User>): Promise<User> {
		// Use apiFetch which automatically handles 401 responses
		const response = await apiFetch("/api/v3/auth/users/me", {
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(data),
		});

		if (!response.ok) {
			throw new Error("Failed to update user");
		}

		try {
			return await response.json();
		} catch (jsonError) {
			console.error("updateUser response is not valid JSON:", jsonError);
			throw new Error("Server returned invalid response format");
		}
	}

	// Get Google OAuth URL with redirect to our callback page
	getGoogleOAuthUrl(): string {
		const callbackUrl = `${window.location.origin}/auth/google/callback`;
		const params = new URLSearchParams({
			frontend_redirect: callbackUrl,
		});
		return `${this.getV3AuthBaseUrl()}/google/authorize?${params.toString()}`;
	}

	// Forgot password
	async forgotPassword(email: string): Promise<void> {
		let response: Response;
		try {
			response = await fetch(`${this.getV3AuthBaseUrl()}/forgot-password`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ email }),
			});
		} catch (networkError) {
			console.error("Network error during forgot password:", networkError);
			throw new Error(
				"Cannot connect to server. Please check if the backend is running.",
			);
		}

		if (!response.ok) {
			let errorMessage = "We could not send the reset email. Please try again.";
			try {
				const error = await response.json();
				// A refusal with no resolvable workspace (DOP-222) arrives as
				// `{code, message}`; `error.detail || …` used to put
				// "[object Object]" on the register screen.
				// A bare fastapi-users code gets mapped to copy, never shown raw.
				errorMessage = authErrorMessage(error.detail, errorMessage);
			} catch (jsonError) {
				// If response is not JSON (e.g., HTML error page), use status text
				errorMessage = friendlyStatusMessage(response.status);
			}
			throw new Error(errorMessage);
		}
	}

	// Reset password using token
	async resetPassword(token: string, password: string): Promise<void> {
		let response: Response;
		try {
			response = await fetch(`${this.getV3AuthBaseUrl()}/reset-password`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ token, password }),
			});
		} catch (networkError) {
			console.error("Network error during reset password:", networkError);
			throw new Error(
				"Cannot connect to server. Please check if the backend is running.",
			);
		}

		if (!response.ok) {
			let errorMessage = "We could not reset that password. Please try again.";
			try {
				const error = await response.json();
				// A refusal with no resolvable workspace (DOP-222) arrives as
				// `{code, message}`; `error.detail || …` used to put
				// "[object Object]" on the register screen.
				// A bare fastapi-users code gets mapped to copy, never shown raw.
				errorMessage = authErrorMessage(error.detail, errorMessage);
			} catch (jsonError) {
				errorMessage = friendlyStatusMessage(response.status);
			}
			throw new Error(errorMessage);
		}
	}
}

export const authService = new AuthService();
